import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Button, ButtonColor } from '../../../common/ui-kit/buttons/Button';
import { FormComponentProps } from '../../../global-forms/GlobalForms.types';
import styles from './NoteCreatingOrEditingForm.module.scss';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';

import { QueryFactory } from '@lmt/shared-types';
import { useForm } from 'react-hook-form';
import {
  convertToErrorString,
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { defaultNoteData } from '@lmt/unicorn-shared/app/components/calendar/notes/form/common';
import { RadioButton } from '../../../common/ui-kit/RadioButton';
import { NoteFormData, NoteType } from './types';
import { Field } from '../../../common/Field';
import { CheckBox } from '../../../common/ui-kit/CheckBox';
import { Input } from '../../../common/ui-kit/inputs/Input';
import {
  maxLengthRule,
  requiredRule,
  useResetFormWhenDataIsLoaded,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { HookFormDatePicker } from '../../../common/ui-kit/inputs/date-time/HookFormDatePicker';
import { Loading } from '../../../common/Loading';
import { addDays } from 'date-fns';

interface EditNoteProps {
  noteId?: number;
}

export const CreateOrEditNote: FC<FormComponentProps & EditNoteProps> = (
  props,
) => {
  const { noteId, formType, formId, tryCompleteForm, loadBlocking } = props;
  const i18next = useScopedTranslation('notes.form');
  const modals = useModal();

  const noteQuery = QueryFactory.NoteQuery.useGetNoteQuery(noteId!, {
    enabled: !!noteId,
  });

  const form = useForm<NoteFormData>({
    shouldFocusError: true,
    criteriaMode: 'all',
    defaultValues: defaultNoteData,
  });

  const {
    register,
    formState,
    watch,
    setError,
    handleSubmit,
    control,
    setValue,
  } = form;

  const [noteType, startDate, endDate] = watch([
    'noteType',
    'startDate',
    'endDate',
  ]);

  const minEndDate = useMemo(() => {
    if (startDate) {
      return addDays(startDate, 1);
    }
    return;
  }, [startDate]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }
    if (startDate >= endDate) {
      setValue('endDate', addDays(startDate, 1));
    }
  }, [startDate]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }
    if (startDate >= endDate) {
      setValue('startDate', addDays(endDate, -1));
    }
  }, [endDate]);

  const noteData = useMemo(() => {
    if (!noteQuery.data) {
      return defaultNoteData;
    }
    const startDate = noteQuery.data.startDate;
    const endDate = noteQuery.data.endDate;
    const noteType = !startDate
      ? NoteType.NoDate
      : !endDate
        ? NoteType.OneDay
        : NoteType.Period;

    return {
      ...noteQuery.data,
      noteType: noteType,
    };
  }, [noteQuery.data]);

  useResetFormWhenDataIsLoaded(form, noteData);

  const deleteNote = useCallback(async () => {
    if (!noteId) {
      return;
    }
    const isConfirmed = await modals.showConfirm({
      title: i18next.t('confirm_delete_title'),
      text: i18next.t('confirm_delete_text'),
      okButtonText: i18next.t('confirm_delete_button'),
    });
    if (isConfirmed) {
      loadBlocking(formId, i18next.t('loading_delete'), async () => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            await QueryFactory.NoteClient.removeNote(noteId);
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }).catch((e) => console.error('NoteCreating.deleteNote', e));
    }
  }, [formType, formId, tryCompleteForm, noteId]);

  const submitForm = useErrorHandler<NoteFormData>(
    useCallback(async (data) => {
      try {
        const dto = {
          text: data.text,
          urgent: data.urgent,
          startDate: data.noteType === NoteType.NoDate ? null : data.startDate,
          endDate: data.noteType === NoteType.Period ? data.endDate : null,
        };

        await tryCompleteForm(formType, formId, async () => {
          if (noteId) {
            await QueryFactory.NoteClient.updateNote(noteId, dto);
          } else {
            await QueryFactory.NoteClient.createNote(dto);
          }
        });
      } catch (e) {
        const errorDetails = handleSubmitFormError(e, setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }, []),
    setError,
  );

  return (
    <Loading loading={noteQuery.isLoading}>
      <form
        className={styles.noteForm}
        onSubmit={handleSubmit(submitForm.handler)}
      >
        <div className="globalFormContentRoot">
          <div className="globalFormContentBody">
            <div className={styles.section}>
              <Field
                title={i18next.t('text')}
                required={true}
                className={styles.field}
              >
                <Input
                  {...register('text', {
                    ...requiredRule(),
                    ...maxLengthRule(1024),
                  })}
                  className={styles.input}
                  errorText={formState.errors.text?.message}
                />
              </Field>
              <Field title="" className={styles.checkbox} alignCheckbox={true}>
                <CheckBox {...register('urgent')} title={i18next.t('urgent')} />
              </Field>
            </div>
            <div className="globalFormsContentSeparator" />
            <div className={styles.radioButtonsContainer}>
              <RadioButton
                name={'one-day-radio-button'}
                title={i18next.t('one_day')}
                checked={noteType === NoteType.OneDay}
                onChange={() => setValue('noteType', NoteType.OneDay)}
                className={styles.radioButton}
              />
              <RadioButton
                name={'period-radio-button'}
                title={i18next.t('period')}
                checked={noteType === NoteType.Period}
                onChange={() => setValue('noteType', NoteType.Period)}
                className={styles.radioButton}
              />
              <RadioButton
                name={'no-date-radio-button'}
                title={i18next.t('no_date')}
                checked={noteType === NoteType.NoDate}
                onChange={() => setValue('noteType', NoteType.NoDate)}
                className={styles.radioButton}
              />
            </div>
            <div className={styles.dateRange}>
              {noteType !== NoteType.NoDate && (
                <Field
                  title={i18next.t(
                    noteType === NoteType.OneDay ? 'date' : 'start_date',
                  )}
                  required={true}
                >
                  <HookFormDatePicker
                    name={'startDate'}
                    control={control}
                    rules={requiredRule()}
                    error={!!formState.errors.startDate}
                    errorText={(formState.errors.startDate as any)?.message}
                  />
                </Field>
              )}
              {noteType === NoteType.Period && (
                <>
                  <div className={styles.range}>-</div>
                  <Field title={i18next.t('end_date')} required={true}>
                    <HookFormDatePicker
                      minDate={minEndDate}
                      name={'endDate'}
                      control={control}
                      rules={requiredRule()}
                      error={!!formState.errors.endDate}
                      errorText={(formState.errors.endDate as any)?.message}
                    />
                  </Field>
                </>
              )}
            </div>
          </div>
          <div className={styles.footer}>
            <div>
              {noteId && (
                <Button
                  color={ButtonColor.Danger}
                  title={i18next.t('delete_button')}
                  onClick={deleteNote}
                  className="globalFormContentSubmitButton"
                />
              )}
            </div>
            <SubmitButton
              title={i18next.t('save_button')}
              submitting={formState.isSubmitting}
              className="globalFormContentSubmitButton"
            />
          </div>
        </div>
      </form>
    </Loading>
  );
};
